// pages/HelpSupport.jsx
import React, { useState, useEffect } from 'react';
import { 
HelpCircle, MessageSquare, BookOpen, Search, FileText,
ChevronRight, Home, Zap, ExternalLink
} from 'lucide-react';
import Header from '@/components/ui/Header';
import CreateTicket from './components/CreateTicket';
import MyTickets from './components/MyTickets';
import TicketDetails from './components/TicketDetails';
import FAQ from './components/FAQ';
import SupportStats from './components/SupportStats';
import apiRequest from '@/services/requests';

const TABS = [
{ 
id: 'overview', 
label: 'Overview', 
icon: HelpCircle,
description: 'Quick stats & FAQs',
color: 'blue'
},
{ 
id: 'new-ticket', 
label: 'New Ticket', 
icon: MessageSquare,
description: 'Create support request',
color: 'green'
},
{ 
id: 'my-tickets', 
label: 'My Tickets', 
icon: FileText,
description: 'View ticket history',
color: 'purple'
},
{ 
id: 'faq', 
label: 'FAQ', 
icon: BookOpen,
description: 'Common questions',
color: 'amber'
},
];

const HelpSupport = () => {
const [activeTab, setActiveTab] = useState('overview');
const [selectedTicket, setSelectedTicket] = useState(null);
const [stats, setStats] = useState(null);
const [loading, setLoading] = useState(true);
const [isTransitioning, setIsTransitioning] = useState(false);
const [isMobile, setIsMobile] = useState(false);

useEffect(() => {
loadStats();
const checkMobile = () => setIsMobile(window.innerWidth < 768);
checkMobile();
window.addEventListener('resize', checkMobile);
return () => window.removeEventListener('resize', checkMobile);
}, []);

const loadStats = async () => {
setLoading(true);
try {
const res = await apiRequest.getSupportStats();
if (res.success) {
setStats(res.data);
}
} catch (error) {
console.error('Error loading stats:', error);
}
setLoading(false);
};

const handleTabChange = (tabId) => {
setIsTransitioning(true);
setSelectedTicket(null);
setTimeout(() => {
setActiveTab(tabId);
setIsTransitioning(false);
window.scrollTo({ top: 0, behavior: 'smooth' });
}, 150);
};

const handleTicketClick = (ticket) => {
setSelectedTicket(ticket);
setActiveTab('ticket-details');
};

const handleTicketCreated = () => {
loadStats();
setActiveTab('my-tickets');
};

const renderTabContent = () => {
if (selectedTicket && activeTab === 'ticket-details') {
return (
<TicketDetails 
ticket={selectedTicket} 
onBack={() => {
setSelectedTicket(null);
setActiveTab('my-tickets');
}}
/>
);
}

const content = (() => {
switch (activeTab) {
case 'overview':
return (
<div className="space-y-6">
<SupportStats stats={stats} loading={loading} />
<FAQ compact={true} onCreateTicket={() => setActiveTab('new-ticket')} />
</div>
);
case 'new-ticket':
return <CreateTicket onSuccess={handleTicketCreated} />;
case 'my-tickets':
return <MyTickets onTicketClick={handleTicketClick} onRefresh={loadStats} />;
case 'faq':
return <FAQ onCreateTicket={() => setActiveTab('new-ticket')} />;
default:
return null;
}
})();

return (
<div 
className={`transition-all duration-150 ${
isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
}`}
>
{content}
</div>
);
};

const activeTabData = TABS.find(tab => tab.id === activeTab);

return (
<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
<Header />

<main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
<div className="max-w-7xl mx-auto">
{/* Breadcrumb */}
{/*<div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
<Home size={16} />
<ChevronRight size={14} />
<span>Help & Support</span>
{activeTabData && (
<>
<ChevronRight size={14} />
<span className="text-blue-600 font-medium">{activeTabData.label}</span>
</>
)}
</div>*/}

{/* Enhanced Header */}
<div className="mb-8">
<div className="flex items-center gap-4 mb-6">
<div className="relative">
<div className="p-2 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl shadow-xl">
<HelpCircle className="text-white" size={16} />
</div>
<div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl animate-pulse opacity-20"></div>
</div>
<div>
<h1 className="text-xl lg:text-3xl font-bold text-slate-900 flex items-center gap-3">
Help & Support
</h1>
<p className="text-slate-600 text-sm mt-1">
We're here to help you grow your career.
</p>
</div>
</div>

{/* Quick Stats Bar */}
{!loading && stats && (
<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
<div className="bg-white rounded-xl border border-slate-200 px-4 py-3 shadow-sm">
<p className="text-xs text-slate-600 mb-1">Total Tickets</p>
<p className="text-2xl font-bold text-slate-900">{stats.total}</p>
</div>

<div className="bg-green-50 rounded-xl border border-green-200 px-4 py-3 shadow-sm">
<p className="text-xs text-green-700 mb-1">Open</p>
<p className="text-2xl font-bold text-green-900">{stats.byStatus.open}</p>
</div>

<div className="bg-blue-50 rounded-xl border border-blue-200 px-4 py-3 shadow-sm">
<p className="text-xs text-blue-700 mb-1">In Progress</p>
<p className="text-2xl font-bold text-blue-900">{stats.byStatus.inProgress}</p>
</div>

<div className="bg-purple-50 rounded-xl border border-purple-200 px-4 py-3 shadow-sm">
<p className="text-xs text-purple-700 mb-1">Resolved</p>
<p className="text-2xl font-bold text-purple-900">{stats.byStatus.resolved}</p>
</div>
</div>
)}
</div>

{/* Tab Navigation */}
<div className="mb-8">
<div className={`
${isMobile 
? 'flex flex-col gap-2' 
: 'flex gap-2 p-1.5 bg-white rounded-xl shadow-sm border border-slate-200'
}
`}>
{TABS.map((tab, index) => {
const Icon = tab.icon;
const isActive = activeTab === tab.id;

return (
<button
key={tab.id}
onClick={() => handleTabChange(tab.id)}
className={`
flex items-center gap-3 px-6 py-4 rounded-xl font-semibold transition-all duration-200
${isMobile ? 'w-full justify-between' : ''}
${isActive 
? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg transform scale-105' 
: 'text-slate-600 hover:bg-slate-50 bg-white border border-slate-200'
}
`}
style={{
animationDelay: `${index * 50}ms`
}}
>
<div className="flex items-center gap-3">
<Icon size={20} />
<div className="text-left">
<p className={`font-bold ${isActive ? 'text-white' : 'text-slate-900'}`}>
{tab.label}
</p>
{isMobile && (
<p className={`text-xs ${isActive ? 'text-white/80' : 'text-slate-500'}`}>
{tab.description}
</p>
)}
</div>
</div>
{isActive && isMobile && <ChevronRight size={20} />}
</button>
);
})}
</div>

{/* Active tab description for desktop */}
{!isMobile && activeTabData && (
<div className="mt-4 flex items-center gap-2 text-sm text-slate-600 bg-white px-4 py-3 rounded-xl border border-slate-200 shadow-sm">
<Zap size={16} className="text-blue-600" />
<span>{activeTabData.description}</span>
</div>
)}
</div>

{/* Content */}
<div className="min-h-[500px]">
{renderTabContent()}
</div>

{/* Help Resources Footer */}
{/*<div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
<h3 className="font-bold text-slate-900 mb-4">Additional Resources</h3>
<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<a 
href="#" 
className="flex items-center gap-3 p-4 bg-white rounded-lg hover:shadow-md transition-all group"
>
<BookOpen className="text-blue-600" size={24} />
<div>
<p className="font-semibold text-slate-900 group-hover:text-blue-600">Documentation</p>
<p className="text-xs text-slate-500">Detailed guides & tutorials</p>
</div>
<ExternalLink className="ml-auto text-slate-400 group-hover:text-blue-600" size={16} />
</a>

<a 
href="#" 
className="flex items-center gap-3 p-4 bg-white rounded-lg hover:shadow-md transition-all group"
>
<MessageSquare className="text-green-600" size={24} />
<div>
<p className="font-semibold text-slate-900 group-hover:text-green-600">Live Chat</p>
<p className="text-xs text-slate-500">Chat with our team</p>
</div>
<ExternalLink className="ml-auto text-slate-400 group-hover:text-green-600" size={16} />
</a>

<a 
href="#" 
className="flex items-center gap-3 p-4 bg-white rounded-lg hover:shadow-md transition-all group"
>
<Search className="text-purple-600" size={24} />
<div>
<p className="font-semibold text-slate-900 group-hover:text-purple-600">Knowledge Base</p>
<p className="text-xs text-slate-500">Search articles</p>
</div>
<ExternalLink className="ml-auto text-slate-400 group-hover:text-purple-600" size={16} />
</a>
</div>
</div>*/}

<div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
<h3 className="font-bold text-slate-900 mb-4">Additional Resources</h3>
<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<a 
href="/documentation" 
className="flex items-center gap-3 p-4 bg-white rounded-lg hover:shadow-md transition-all group"
>
<BookOpen className="text-blue-600" size={24} />
<div>
<p className="font-semibold text-slate-900 group-hover:text-blue-600">Documentation</p>
<p className="text-xs text-slate-500">Detailed guides & tutorials</p>
</div>
<ExternalLink className="ml-auto text-slate-400 group-hover:text-blue-600" size={16} />
</a>

{/* Live Chat Button - Opens the floating widget */}
<button 
onClick={() => {
// Trigger the global chat widget
// You can use a context or event to open it
window.dispatchEvent(new CustomEvent('openLiveChat'));
}}
className="flex items-center gap-3 p-4 bg-white rounded-lg hover:shadow-md transition-all group text-left w-full"
>
<MessageSquare className="text-green-600" size={24} />
<div>
<p className="font-semibold text-slate-900 group-hover:text-green-600">Live Chat</p>
<p className="text-xs text-slate-500">Chat with our team</p>
</div>
<div className="ml-auto flex items-center gap-2">
<span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
<span className="text-xs font-medium text-green-600">Online</span>
</div>
</button>

<a 
href="/knowledge-base" 
className="flex items-center gap-3 p-4 bg-white rounded-lg hover:shadow-md transition-all group"
>
<Search className="text-purple-600" size={24} />
<div>
<p className="font-semibold text-slate-900 group-hover:text-purple-600">Knowledge Base</p>
<p className="text-xs text-slate-500">Search articles</p>
</div>
<ExternalLink className="ml-auto text-slate-400 group-hover:text-purple-600" size={16} />
</a>
</div>
</div>



</div>
</main>
</div>
);
};

export default HelpSupport;